import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import api from './api';

// Отдельный компонент для дочерней шапки Аккордеона
function AccordionHeader({ title, count, isOpen, onClick, bgStyle, textStyle }) {
  return (
    <button 
      onClick={onClick}
      className={`flex justify-between items-center w-full py-4 px-5 rounded-2xl mb-3 font-semibold transition-all duration-200 shadow-md border ${bgStyle} ${textStyle} cursor-pointer`}
    >
      <div className="flex items-center gap-2.5">
        <span className="uppercase tracking-widest font-bold text-xs">{title}</span>
        <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-slate-900/40 text-white shadow-inner border border-white/5">
          {count}
        </span>
      </div>
      <span className={`text-xs transition-transform duration-300 transform ${isOpen ? 'rotate-180' : ''}`}>▼</span>
    </button>
  );
}

// Отдельный компонент для одной задачи с таймером в темной теме
function TaskCard({ task, onCancel, onSelect }) {
  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    const formatAndSetTime = (ms) => {
      const h = Math.floor(ms / (1000 * 60 * 60));
      const m = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
      const s = Math.floor((ms % (1000 * 60)) / 1000);
      setTimeLeft(`${h}ч ${m}м ${s}с`);
    };

    const timer = setInterval(() => {
      const now = new Date().getTime();
      let distance = 0;

      if (task.status === 'in_progress' && task.taken_at) {
        const startTime = new Date(task.taken_at).getTime();
        const deadline = startTime + (task.deadline_hours * 60 * 60 * 1000);
        distance = deadline - now;

        if (distance < 0) {
          clearInterval(timer);
          setTimeLeft("Время вышло!");
        } else {
          formatAndSetTime(distance);
        }
      } else if (task.status === 'review' && (task.report_created_at || task.report?.created_at)) {
        const reportTime = new Date(task.report_created_at || task.report.created_at).getTime();
        const autoApproveDeadline = reportTime + (36 * 60 * 60 * 1000);
        distance = autoApproveDeadline - now;

        if (distance < 0) {
          clearInterval(timer);
          setTimeLeft("Автопринято системой!");
        } else {
          formatAndSetTime(distance);
        }
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [task]);

  const rubPrice = task.price_rub || Math.round(task.price * 90);

  return (
    <div className="group p-6 bg-[#1a2642]/40 border border-slate-800/60 rounded-3xl shadow-xl hover:shadow-black/20 hover:-translate-y-0.5 transition-all duration-300 relative overflow-hidden">
      
      {/* Декоративный левый маркер статуса */}
      <div className={`absolute left-0 top-0 bottom-0 w-[4px] 
        ${task.status === 'review' ? 'bg-amber-500' : 'bg-indigo-500'}`} 
      />

      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 pl-1">
        <div className="space-y-1">
          <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-0.5 rounded-md
            ${task.category === 'AI' ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20' : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'}`}>
            {task.category === 'AI' ? '🤖 ИИ и Нейросети' : '⚡ Микро-задача'}
          </span>
          <h3 className="font-bold text-xl text-white tracking-tight leading-snug group-hover:text-indigo-400 transition-colors pt-1">
            {task.title}
          </h3>
        </div>

        {/* ТЕМНО-СЕРЫЙ таймер дедлайна */}
        <div className="flex flex-col items-start sm:items-end flex-shrink-0">
          <span className="text-[10px] text-slate-400 uppercase font-black tracking-wider block mb-1">
            {task.status === 'review' ? '🔒 Автоприемка через' : '⏱️ Дедлайн'}
          </span>
          <div className="font-mono text-sm font-bold bg-slate-900 text-slate-100 px-3 py-1.5 rounded-xl border border-slate-800 shadow-inner tracking-wide">
            {timeLeft || "расчет..."}
          </div>
        </div>
      </div>

      {/* Описание */}
      <div className="text-base text-slate-200 mt-4 bg-[#11192e]/50 p-5 rounded-2xl border border-slate-800/40 leading-relaxed pl-1 max-h-60 overflow-y-auto whitespace-pre-line custom-scroll text-left">
        {task.description}
      </div>
      
      {/* Блок доработки от заказчика */}
      {task.status === 'in_progress' && task.revision_comment && (
        <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/20 rounded-2xl pl-1">
          <p className="text-amber-400 font-bold text-xs uppercase tracking-wider mb-1 flex items-center gap-1.5">
            ⚠️ Комментарий заказчика по доработке:
          </p>
          <p className="whitespace-pre-line text-amber-300 text-sm leading-relaxed">{task.revision_comment}</p>
        </div>
      )}
      
      {/* Нижняя панель действий */}
      <div className="mt-6 pt-4 border-t border-slate-800/60 flex flex-col sm:flex-row justify-between sm:items-center gap-4 pl-1">
        <div className="flex items-center gap-2.5 w-full sm:w-auto flex-1">
          
          {task.status === 'in_progress' ? (
            <>
              <button 
                onClick={() => onSelect(task)} 
                className="flex-1 sm:flex-none text-sm bg-indigo-600 text-white px-5 py-3 rounded-xl font-bold hover:bg-indigo-500 transition shadow-lg shadow-indigo-600/10 active:scale-[0.98] cursor-pointer"
              >
                📤 Открыть / Сдать работу
              </button>
              
              <button 
                onClick={() => onCancel(task.id)} 
                className="text-sm bg-rose-500/10 text-rose-400 px-4 py-3 rounded-xl font-semibold border border-rose-500/20 hover:bg-rose-500/20 hover:text-rose-300 transition active:scale-[0.98] cursor-pointer"
              >
                Отказаться
              </button>
            </>
          ) : (
            <div className="text-xs font-semibold text-amber-400 bg-amber-500/10 border border-amber-500/20 px-4 py-3 rounded-xl flex items-center gap-2 w-full sm:w-auto">
              ⏳ Отчет отправлен на проверку заказчику
            </div>
          )}
        </div>

        {/* Оплата */}
        <div className="flex items-center sm:justify-end gap-4 text-right flex-shrink-0">
          <div>
            <span className="text-[10px] text-slate-400 font-bold block uppercase tracking-wider">Оплата</span>
            <span className="text-lg font-black text-emerald-400">{task.price} USDT</span>
            <span className="text-[11px] text-slate-400 block font-medium mt-0.5">
              ≈ {rubPrice} ₽
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Основной компонент
function MyTasks({ refreshTrigger, onTaskStatusChange }) {
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [reportData, setReportData] = useState({ text: '', link: '', images: [] });
  const [lastSavedTime, setLastSavedTime] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isOpenReview, setIsOpenReview] = useState(true);
  const [isOpenActive, setIsOpenActive] = useState(true);

  const reportDataRef = useRef(reportData);
  useEffect(() => {
    reportDataRef.current = reportData;
  }, [reportData]);

  const loadTasks = useCallback(() => {
    api.get('tasks/my-active/')
      .then(res => setTasks(res.data))
      .catch(err => console.error("Ошибка загрузки активных задач", err));
  }, []);

  useEffect(() => { 
    loadTasks(); 
  }, [loadTasks, refreshTrigger]);

  // Восстановление черновика при открытии модалки
  useEffect(() => {
    if (selectedTask) {
      const savedDraft = localStorage.getItem(`task_draft_${selectedTask.id}`);
      if (savedDraft) {
        try {
          const parsed = JSON.parse(savedDraft);
          setReportData({
            text: parsed.text || '',
            link: parsed.link || '',
            images: []
          });
          if (parsed.savedAt) {
            setLastSavedTime(parsed.savedAt);
          }
        } catch (e) {
          console.error("Ошибка чтения черновика", e);
        }
      } else {
        setReportData({ text: '', link: '', images: [] });
        setLastSavedTime(null);
      }
    }
  }, [selectedTask]);

  // Функция сохранения в localStorage
  const saveDraft = () => {
    if (!selectedTask) return;
    const currentData = reportDataRef.current;
    if (!currentData.text && !currentData.link) return;

    const timeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const draftPayload = {
      text: currentData.text,
      link: currentData.link,
      savedAt: timeStr
    };

    localStorage.setItem(`task_draft_${selectedTask.id}`, JSON.stringify(draftPayload));
    setLastSavedTime(timeStr);
  };

  // Интервал сохранения каждые 20 секунд
  useEffect(() => {
    if (!selectedTask) return;

    const interval = setInterval(() => {
      saveDraft();
    }, 20000);

    return () => clearInterval(interval);
  }, [selectedTask]);

  const clearDraft = (taskId) => {
    if (taskId) {
      localStorage.removeItem(`task_draft_${taskId}`);
    }
    setLastSavedTime(null);
  };

  const handleCancel = async (taskId) => {
    if (window.confirm("Вы уверены, что хотите отказаться от выполнения этого заказа? Это снизит ваш текущий рейтинг.")) {
      try {
        await api.patch(`tasks/${taskId}/cancel/`);
        alert("Вы успешно отказались от задачи.");
        loadTasks();
        if (onTaskStatusChange) onTaskStatusChange();
      } catch (err) { alert("Ошибка при отмене заказа."); }
    }
  };

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    saveDraft();
    setIsSubmitting(true);

    const formData = new FormData();
    formData.append('task', selectedTask.id);
    formData.append('text', reportData.text);
    formData.append('link', reportData.link);
    
    if (reportData.images.length > 0) {
      formData.append('image', reportData.images[0]);
    }

    reportData.images.forEach((file) => {
      formData.append('uploaded_images', file);
    });

    try {
      await api.post('tasks/report/', formData, { headers: { 'Content-Type': 'multipart/form-data' }});
      alert("Работа успешно сдана на проверку заказчику!");
      clearDraft(selectedTask.id);
      setSelectedTask(null);
      setReportData({ text: '', link: '', images: [] });
      loadTasks(); 
      if (onTaskStatusChange) onTaskStatusChange();
    } catch (err) { 
      alert("Ошибка при отправке отчета."); 
    } finally {
      setIsSubmitting(false);
    }
  };

  const reviewTasks = tasks.filter(t => t.status === 'review');
  const activeTasks = tasks.filter(t => t.status === 'in_progress');

  return (
    <div className="w-full max-w-2xl mx-auto font-sans text-slate-200">
      <Helmet>
        <title>Мои взять задачи — RealWork.pro</title>
        <meta name="description" content="Управление взятыми заказами и сдача отчетов заказчикам." />
      </Helmet>

      {/* Блок На проверке */}
      {reviewTasks.length > 0 && (
        <div className="mb-5">
          <AccordionHeader 
            title="Ожидают проверки заказчиком" 
            count={reviewTasks.length} 
            isOpen={isOpenReview} 
            onClick={() => setIsOpenReview(!isOpenReview)} 
            bgStyle="bg-amber-500/10 border-amber-500/20"
            textStyle="text-amber-400 hover:bg-amber-500/20"
          />
          <div className={`space-y-4 transition-all duration-300 ease-in-out overflow-hidden ${isOpenReview ? 'max-h-[1500px] opacity-100 mt-2 mb-4' : 'max-h-0 opacity-0'}`}>
            {reviewTasks.map(task => (
              <TaskCard key={task.id} task={task} onCancel={handleCancel} onSelect={setSelectedTask} />
            ))}
          </div>
        </div>
      )}

      {/* Блок В работе */}
      <div className="mb-5">
        <AccordionHeader 
          title="Выполняются прямо сейчас" 
          count={activeTasks.length} 
          isOpen={isOpenActive} 
          onClick={() => setIsOpenActive(!isOpenActive)} 
          bgStyle="bg-indigo-500/10 border-indigo-500/20"
          textStyle="text-indigo-400 hover:bg-indigo-500/20"
        />
        <div className={`space-y-4 transition-all duration-300 ease-in-out overflow-hidden ${isOpenActive ? 'max-h-[2000px] opacity-100 mt-2 mb-4' : 'max-h-0 opacity-0'}`}>
          {activeTasks.length > 0 ? activeTasks.map(task => (
            <TaskCard key={task.id} task={task} onCancel={handleCancel} onSelect={setSelectedTask} />
          )) : <p className="text-xs text-slate-400 italic pl-2 py-2">У вас пока нет активных задач на исполнении.</p>}
        </div>
      </div>

      {/* УВЕЛИЧЕННОЕ ДВУХКОЛОНОЧНОЕ МОДАЛЬНОЕ ОКНО С АВТОСОХРАНЕНИЕМ */}
      {selectedTask && (
        <div className="fixed inset-0 bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 z-50 animate-in fade-in duration-200">
          <div className="bg-[#111827] rounded-[32px] w-full max-w-6xl shadow-[0_25px_70px_rgba(0,0,0,0.7)] border border-slate-800 animate-in zoom-in-95 duration-200 text-white overflow-hidden flex flex-col max-h-[92vh]">
            
            {/* Шапка модального окна */}
            <div className="p-5 sm:p-7 border-b border-slate-800/80 flex justify-between items-start bg-[#161f32]/60">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-2xl">✨</span>
                  <h2 className="font-black text-2xl text-white tracking-tight">Сдача проекта</h2>
                </div>
                <p className="text-xs sm:text-sm text-slate-400 font-medium pl-9">
                  Отправка результатов по задаче: <span className="font-bold text-slate-200">{selectedTask.title}</span>
                </p>
              </div>
              <button 
                onClick={() => setSelectedTask(null)}
                className="text-slate-400 hover:text-white text-3xl font-bold p-1 leading-none transition-colors cursor-pointer"
              >
                &times;
              </button>
            </div>

            {/* ДВЕ КОЛОНКИ: ТЗ (5 колонок), Форма отчета (7 колонок) */}
            <div className="grid grid-cols-1 lg:grid-cols-12 divide-y lg:divide-y-0 lg:divide-x divide-slate-800/80 overflow-hidden">
              
              {/* Левая колонка: ТЗ */}
              <div className="lg:col-span-5 p-6 sm:p-8 overflow-y-auto max-h-[40vh] lg:max-h-[75vh] bg-[#0d1320]/60 space-y-4 custom-scroll">
                <span className="text-xs font-black text-indigo-400 uppercase tracking-wider block">
                  📋 Условия задания (ТЗ)
                </span>
                <div className="text-sm text-slate-300 leading-relaxed whitespace-pre-line text-left">
                  {selectedTask.description || "Описание отсутствует."}
                </div>

                {selectedTask.revision_comment && (
                  <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/20 rounded-2xl text-left">
                    <p className="text-amber-400 font-bold text-xs uppercase tracking-wider mb-1">
                      ⚠️ Комментарий по доработке:
                    </p>
                    <p className="whitespace-pre-line text-amber-300 text-xs leading-relaxed">{selectedTask.revision_comment}</p>
                  </div>
                )}
              </div>

              {/* Правая колонка: Форма отчета */}
              <form onSubmit={handleReportSubmit} className="lg:col-span-7 p-6 sm:p-8 space-y-5 overflow-y-auto max-h-[50vh] lg:max-h-[75vh] custom-scroll flex flex-col justify-between bg-[#111827]">
                <div className="space-y-5">
                  
                  {/* Текстовый отчёт c надписью об автосохранении */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <label className="block text-xs font-black text-slate-300 uppercase tracking-wider">
                        Текстовый отчёт / Результат работы
                      </label>
                      {lastSavedTime && (
                        <span className="text-[11px] font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-0.5 rounded-full flex items-center gap-1.5 animate-in fade-in">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                          Черновик сохранён в {lastSavedTime}
                        </span>
                      )}
                    </div>
                    <textarea 
                      rows="7" 
                      required
                      disabled={isSubmitting}
                      className="w-full border border-slate-700 p-4 rounded-2xl text-sm sm:text-base placeholder:text-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 bg-[#1a2436] text-white transition-all resize-y min-h-[160px] whitespace-pre-wrap" 
                      placeholder="Вставьте тексты, описание выполненных шагов, логов или развернутый ответ заказчику..." 
                      value={reportData.text}
                      onChange={(e) => {
                        setReportData({...reportData, text: e.target.value});
                        saveDraft();
                      }} 
                    />
                  </div>

                  {/* Ссылка */}
                  <div className="space-y-2">
                    <label className="block text-xs font-black text-slate-300 uppercase tracking-wider">
                      Ссылка на готовые материалы (Google Drive / Figma / GitHub / Telegram)
                    </label>
                    <input 
                      disabled={isSubmitting}
                      className="w-full h-13 border border-slate-700 px-4 py-3 rounded-xl text-sm sm:text-base placeholder:text-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 bg-[#1a2436] text-white transition-all" 
                      placeholder="https://drive.google.com/drive/folders/..." 
                      value={reportData.link}
                      onChange={(e) => {
                        setReportData({...reportData, link: e.target.value});
                        saveDraft();
                      }} 
                    />
                  </div>

                  {/* Скриншоты */}
                  <div className="space-y-2">
                    <label className="block text-xs font-black text-slate-300 uppercase tracking-wider">
                      Прикрепить скриншоты или файлы
                    </label>
                    <div className="w-full border border-slate-700 px-4 py-3 rounded-xl flex items-center bg-[#1a2436] focus-within:border-indigo-500 transition-all">
                      <input 
                        type="file" 
                        multiple 
                        disabled={isSubmitting}
                        className="block w-full text-xs sm:text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-indigo-500/20 file:text-indigo-300 hover:file:bg-indigo-500/30 transition-colors cursor-pointer" 
                        onChange={(e) => setReportData({...reportData, images: Array.from(e.target.files)})} 
                      />
                    </div>
                  </div>
                </div>

                {/* Кнопки внизу */}
                <div className="flex flex-col sm:flex-row gap-4 pt-6 mt-4 border-t border-slate-800">
                  <button 
                    type="button" 
                    onClick={() => setSelectedTask(null)} 
                    disabled={isSubmitting}
                    className="py-3.5 px-6 bg-slate-800 text-slate-300 rounded-xl font-bold hover:bg-slate-700 transition-colors text-sm order-2 sm:order-1 active:scale-[0.99] cursor-pointer"
                  >
                    Отмена
                  </button>
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 text-white py-3.5 px-6 rounded-xl font-bold hover:opacity-95 transition-all text-sm sm:text-base order-1 sm:order-2 shadow-lg shadow-emerald-950/30 active:scale-[0.99] flex items-center justify-center gap-2 tracking-wide cursor-pointer disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Отправка отчета...</span>
                      </>
                    ) : (
                      <>
                        <span>Отправить отчёт заказчику</span>
                        <svg className="w-5 h-5 transform rotate-45 -mt-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              </form>

            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyTasks;